/**
 * SetupCompass — WU-3 / R-97 (2026-05-21), restyled as a stepper by
 * R-129 (2026-05-23).
 *
 * Ordered build-flow guide across the two config pages (Job Config — formerly
 * "Processing Config", renamed in WU-10 / R-130 — and Form Config). It is a
 * guide, not a gate — every chip is a plain
 * link; users may jump anywhere. The visual treatment is a numbered
 * stepper rather than a tab strip so the *ordered* nature of the flow is
 * unambiguous:
 *
 *   - Each chip is prefixed with its 1-based position.
 *   - A chevron divider sits between adjacent chips so it reads
 *     left-to-right as a flow.
 *   - Chips have three states:
 *       • completed — every step before the active one
 *       • current   — the active step (also marked aria-current="step")
 *       • upcoming  — every step after the active one (or all of them
 *                     when no step is active, e.g. a tab outside the
 *                     compass surface)
 *   - The active chip is highlighted with the suite copper accent
 *     (#c27040 / var(--color-accent)) per conductor DESIGN.md.
 */
import { Fragment } from "react";
import { Link } from "react-router";
import { type Surface, filterBySurface } from "@capture/shared";
import "./SetupCompass.css";

type OwningPage = "processing" | "form";

interface CompassStep {
  id: string;
  label: string;
  page: OwningPage;
  tab: string;
  /** SP1 §5.1 — absent = shown on both surfaces. */
  surfaces?: readonly Surface[];
}

// F-015 (2026-06-17) — "Sample" runs a sample document to TEST the configured
// job, so it belongs near the END of the build flow (after Rules, just before
// Export/Publish) — not mid-sequence before Zones/Calibration/Rules are even
// set. Moving it removes the "test before you've configured anything" implication.
export const COMPASS_STEPS: CompassStep[] = [
  { id: "general", label: "General", page: "processing", tab: "general" },
  // 2026-06-28 — the old job "Workflow" tab was consolidated into the new
  // "Pipeline" tab (Stages sub-tab) when Pipeline moved to Job Config. The
  // step id/tab must track the live tab id so the chip both links to a real
  // tab and highlights when active (JobTypeDetailPage emits activeStepId
  // "pipeline"). Old ?tab=workflow bookmarks are aliased on that page.
  { id: "pipeline", label: "Pipeline", page: "processing", tab: "pipeline" },
  { id: "fields", label: "Fields", page: "form", tab: "fields" },
  // 2026-06-29 — OCR Profiles + Zones moved to Form Config (rework Tasks 9).
  // Link directly to /admin/forms/:id (page: "form") instead of bouncing
  // through Job Config's stale-tab redirect.
  {
    id: "ocr-profiles",
    label: "OCR Profiles",
    page: "form",
    tab: "ocr-profiles",
  },
  { id: "zones", label: "Zones", page: "form", tab: "zones" },
  {
    id: "calibration",
    label: "Calibration",
    page: "processing",
    tab: "calibration",
  },
  // P3c (2026-07-08) — the legacy Form Config "Rules" tab was retired; rule/
  // automation authoring moved to the Job Config Automations tab.
  // F-002 (2026-07-08) — re-surface that authoring as a build-flow step in the
  // slot the old "Rules" step occupied (after Calibration, before Sample). The
  // step links to Job Config's Automations tab (page "processing" → job-types
  // base → /admin/job-types/:id?tab=automations) so a first-run builder is
  // actually routed to rule authoring instead of having to discover the tab.
  { id: "automations", label: "Automations", page: "processing", tab: "automations" },
  { id: "sample", label: "Sample", page: "form", tab: "sample" },
  { id: "export-mapping", label: "Export", page: "form", tab: "export-mapping" },
];

function stepHref(jobTypeId: string, step: CompassStep): string {
  const base =
    step.page === "processing"
      ? `/admin/job-types/${jobTypeId}`
      : `/admin/forms/${jobTypeId}`;
  return `${base}?tab=${step.tab}`;
}

type StepState = "completed" | "current" | "upcoming";

function resolveStepState(
  stepIndex: number,
  activeStepIndex: number,
): StepState {
  // When no step is active (e.g. a tab outside the compass surface),
  // activeStepIndex is -1 → every step is upcoming so we don't bleed a
  // "completed" cue onto chips just because they live at index 0.
  if (activeStepIndex === -1) return "upcoming";
  if (stepIndex < activeStepIndex) return "completed";
  if (stepIndex === activeStepIndex) return "current";
  return "upcoming";
}

export interface SetupCompassProps {
  jobTypeId: string;
  /** The COMPASS_STEPS id of the currently-open tab, or "" if none. */
  activeStepId: string;
  /** SP1 — the job's surface; steps filter on it. Default keeps old callers standard. */
  surface?: Surface;
}

export function SetupCompass({ jobTypeId, activeStepId, surface = "standard" }: SetupCompassProps) {
  const steps = filterBySurface(COMPASS_STEPS, surface);
  const activeStepIndex = steps.findIndex(
    (s) => s.id === activeStepId,
  );

  return (
    <nav className="setup-compass" aria-label="Setup steps">
      {steps.map((step, index) => {
        const state = resolveStepState(index, activeStepIndex);
        const isCurrent = state === "current";
        const isCompleted = state === "completed";
        const isLast = index === steps.length - 1;

        return (
          <Fragment key={step.id}>
            <Link
              to={stepHref(jobTypeId, step)}
              className={`compass-chip compass-chip--${step.page} compass-chip--${state}`}
              aria-current={isCurrent ? "step" : undefined}
            >
              <span className="compass-chip__index">
                {/* Numeric prefix — the stepper position cue. Completed
                 * steps swap the number for a check; the accessible
                 * name still carries the step's label below. */}
                {isCompleted ? "✓" : index + 1}
              </span>
              <span className="compass-chip__label">{step.label}</span>
            </Link>
            {!isLast && (
              <span
                className="compass-divider"
                aria-hidden="true"
              >
                {/* U+203A SINGLE RIGHT-POINTING ANGLE QUOTATION MARK —
                 * a flow chevron that reads as "next" without competing
                 * visually with the chip borders. */}
                {"›"}
              </span>
            )}
          </Fragment>
        );
      })}
    </nav>
  );
}
